/* eslint-disable no-restricted-syntax -- replacing the raw `<input type="file">` with an
   `@nrx/ui` component emits entirely different JavaScript; under the port's
   frozen-behaviour contract that is out of remit. */
import React, { useRef, useState } from 'react';
import {
  Modal,
  Header,
  Button,
  Form,
  Input,
  Icon,
  Message,
} from 'semantic-ui-react';

import { SUPPORTED_CONTENT_TYPES, SUPPORTED_FORMATS_LABEL } from './useMemberDocumentUpload';

export interface UploadDocumentModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Callback when the modal is closed. */
  onClose: () => void;
  /** Callback when a document is successfully uploaded. Receives subject and the File. */
  onUpload: (subject: string, file: File) => void | Promise<void>;
  /** Whether an upload is in progress. */
  isUploading?: boolean;
  /** Error message to display. */
  error?: string | null;
  /** Callback to clear the error. */
  onClearError?: () => void;
}

/**
 * Modal for uploading new PDF documents.
 * Follows the design: Subject field, file upload, and action buttons.
 */
function UploadDocumentModal({
  open,
  onClose,
  onUpload,
  isUploading = false,
  error = null,
  onClearError,
}: UploadDocumentModalProps) {
  const [subject, setSubject] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!SUPPORTED_CONTENT_TYPES.includes(file.type)) {
        return;
      }
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (subject && selectedFile) {
      await onUpload(subject, selectedFile);
      handleClose();
    }
  };

  const handleClose = () => {
    setSubject('');
    setSelectedFile(null);
    setFileName('');
    onClearError?.();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Header>
        <Icon name="upload" />
        Upload Document
      </Header>
      <Modal.Content>
        {error && (
          <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
        )}
        <Form>
          <Form.Field>
            <label>Subject</label>
            <Input
              placeholder="Enter subject"
              value={subject}
              onChange={(_, { value }) => setSubject(value)}
            />
          </Form.Field>
          <Form.Field>
            <label>Upload File</label>
            <Button
              color="blue"
              icon="upload"
              content="Choose File"
              onClick={handleUploadClick}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept={SUPPORTED_CONTENT_TYPES.join(',')}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            {fileName && (
              <span style={{ marginLeft: '10px' }}>{fileName}</span>
            )}
            <Message info size="small" style={{ marginTop: '8px' }}>
              Supported formats: {SUPPORTED_FORMATS_LABEL}
            </Message>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={isUploading}>Cancel</Button>
        <Button
          color="green"
          onClick={handleSubmit}
          disabled={!subject || !selectedFile || isUploading}
          loading={isUploading}
        >
          <Icon name="checkmark" /> Upload
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default UploadDocumentModal;
